import { useColorModeValue } from '@chakra-ui/react';
import { useContext } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { HistoricalPropertyContext } from './useProjectHistory';
import { ResolvedOrderEntry } from 'src/domain/resolve-order-entry';
import {
  EventType,
  ShapeDeepFragment,
  SheetShapeDeepFragment,
  ShapeFolderDeepFragment,
  OrderDeepFragment,
  ModelsDeepFragment,
  OrderColumnDeepFragment,
  ElementIdentifierDbidType,
} from 'src/gql/graphql';
import type { Sheet } from 'src/hooks/sheets';
import { ChakraColor } from 'src/utils/color-util';
import {
  ForgeViewerContext,
  type DbIdsPerModel,
  type ModelsMap,
} from 'src/components/common/ForgeViewer';
import type { PropertyContext } from 'src/domain/property-operations';
import { resolveProperties } from 'src/domain/property-operations';
import { convertToElementIdentifierDbidType } from 'src/utils/dbid-utils';
import { calculateGeometry } from 'src/domain/geometric-operations';
import { geometricAlgorithmTypes } from 'src/domain/geometry/algorithm-types';
import type { Point } from 'src/domain/geometry/geometric-types';

// Standalone function for detecting attribute changes
export const getAttributeDiff = (
  referenceState: any,
  diff: any,
  urn: string,
  dbId: number
): { new?: string; old?: string } | undefined => {
  if (!diff || !referenceState) return undefined;

  // Check property value changes first (most common case)
  const valueChangeResult = checkPropertyValueChanges(
    diff,
    referenceState,
    urn,
    dbId
  );
  if (valueChangeResult) {
    return valueChangeResult;
  }

  // Check collection-level changes
  const collectionResult = checkCollectionLevelChanges(diff, urn, dbId);
  if (collectionResult) {
    // For collection changes, we want to show as modified, not created/deleted
    // So we return a result that indicates modification
    return {
      old: collectionResult.old || 'exists',
      new: collectionResult.new || 'exists',
    };
  }

  // Check object-level changes
  const objectResult = checkObjectLevelChanges(diff, urn, dbId);
  if (objectResult) {
    // For object changes (creation/deletion of properties), we want to show as modified
    // So we return a result that indicates modification
    return {
      old: objectResult.old || 'exists',
      new: objectResult.new || 'exists',
    };
  }

  return undefined;
};

// Check property value changes (most common case)
const checkPropertyValueChanges = (
  diff: any,
  referenceState: any,
  urn: string,
  dbId: number
): { new?: string; old?: string } | undefined => {
  const propertyPattern = `propertyContext.loadedSparkelProperties.${urn}.`;
  const propertyDiffKeys = Object.keys(diff).filter(
    (key) => key.startsWith(propertyPattern) && key.endsWith('.propertyValue')
  );

  for (const diffKey of propertyDiffKeys) {
    const propertyId = diffKey
      .replace(propertyPattern, '')
      .replace('.propertyValue', '');

    const properties =
      referenceState?.propertyContext?.loadedSparkelProperties?.[urn];
    if (properties && Array.isArray(properties)) {
      const property = properties.find(
        (prop: any) => prop.id === propertyId && prop.dbId === dbId
      );

      if (property) {
        const propertyDiff = diff[diffKey];
        if (
          propertyDiff &&
          (propertyDiff.old !== undefined || propertyDiff.new !== undefined)
        ) {
          return {
            old: propertyDiff.old,
            new: propertyDiff.new,
          };
        }
      }
    }
  }
  return undefined;
};

// Check collection-level changes (when entire properties collection is replaced)
const checkCollectionLevelChanges = (
  diff: any,
  urn: string,
  dbId: number
): { new?: string; old?: string } | undefined => {
  const collectionKey = `propertyContext.loadedSparkelProperties.${urn}`;
  if (!diff[collectionKey]) return undefined;

  const collectionDiff = diff[collectionKey];
  const oldObj = (collectionDiff.old as Record<string, unknown>) || {};
  const newObj = (collectionDiff.new as Record<string, unknown>) || {};

  // Check if any property for this dbId was added or removed
  const oldProps = Object.values(oldObj).filter((p: any) => p?.dbId === dbId);
  const newProps = Object.values(newObj).filter((p: any) => p?.dbId === dbId);

  if (oldProps.length !== newProps.length) {
    // Properties were added or removed
    return {
      old: oldProps.length > 0 ? 'exists' : undefined,
      new: newProps.length > 0 ? 'exists' : undefined,
    };
  }
  return undefined;
};

// Check object-level changes (when entire property objects are added/removed)
const checkObjectLevelChanges = (
  diff: any,
  urn: string,
  dbId: number
): { new?: string; old?: string } | undefined => {
  const propertyPattern = `propertyContext.loadedSparkelProperties.${urn}.`;
  // Match keys that start with the pattern but don't end with .propertyValue
  // This captures keys like: propertyContext.loadedSparkelProperties.{urn}.{propertyId}
  const objectLevelKeys = Object.keys(diff).filter(
    (key) => key.startsWith(propertyPattern) && !key.endsWith('.propertyValue')
  );

  for (const diffKey of objectLevelKeys) {
    const objectDiff = diff[diffKey];

    if (objectDiff) {
      const oldProp = objectDiff.old as any;
      const newProp = objectDiff.new as any;

      // Check if this property belongs to the target dbId
      if (
        (oldProp && oldProp.dbId === dbId) ||
        (newProp && newProp.dbId === dbId)
      ) {
        // Property was created, deleted, or replaced
        return {
          old: oldProp?.propertyValue,
          new: newProp?.propertyValue,
        };
      }
    }
  }
  return undefined;
};

// History status colors for visual diff indication in history mode
export const HISTORY_COLORS = {
  [EventType.Created]: ChakraColor.green300,
  [EventType.Deleted]: ChakraColor.red400,
  [EventType.Modified]: ChakraColor.orange200,
};

export type ShapeWithEventType = (
  | ShapeDeepFragment
  | SheetShapeDeepFragment
) & {
  eventType?: EventType;
};

export const getHistoricalShapes = (
  referenceState: any,
  diff: Record<string, any>,
  currentShapes: (ShapeDeepFragment | SheetShapeDeepFragment)[] | undefined
): ShapeWithEventType[] => {
  // Fix shape geometry structure
  const fixGeometry = (shape: any) => {
    const fixed = { ...shape };

    // Fix polygon multipolygon structure
    if (fixed.polygon?.multipolygon) {
      const mp = fixed.polygon.multipolygon;
      if (typeof mp.polygons === 'string') {
        fixed.polygon.multipolygon = { polygons: parseJson(mp.polygons) };
      } else if (Array.isArray(mp)) {
        fixed.polygon.multipolygon = { polygons: mp };
      }
    }

    // Fix extruded polygon multipolygon structure
    if (fixed.extrudedPolygon?.multipolygon) {
      const mp = fixed.extrudedPolygon.multipolygon;
      if (typeof mp.polygons === 'string') {
        fixed.extrudedPolygon.multipolygon = {
          polygons: parseJson(mp.polygons),
        };
      } else if (Array.isArray(mp)) {
        fixed.extrudedPolygon.multipolygon = { polygons: mp };
      }
    }

    // Fix sheet shape polygon structure
    if (fixed.sheetShapePolygon?.multipolygon) {
      const mp = fixed.sheetShapePolygon.multipolygon;
      if (typeof mp.polygons === 'string') {
        fixed.sheetShapePolygon.multipolygon = {
          polygons: parseJson(mp.polygons),
        };
      }
    }

    return fixed;
  };

  // Parse diff key to extract shape info
  const parseShapeKey = (key: string) => {
    // Match: propertyContext.loadedShapes.{urn}.{id} or propertyContext.loadedSheetShapes.{urn}.{id}
    const shapeMatch = key.match(
      /propertyContext\.(loadedShapes|loadedSheetShapes)\.([^.]+)\.([^.]+)(?:\.(.+))?/
    );
    if (!shapeMatch) return null;

    const [, shapeType, urn, id, propertyPath = ''] = shapeMatch;
    return {
      isSheetShape: shapeType === 'loadedSheetShapes',
      urn,
      id,
      propertyPath,
      uniqueId: `${urn}-${id}`,
    };
  };

  // Collect all shapes with their change status
  const shapes = new Map<
    string,
    {
      shape: ShapeDeepFragment | SheetShapeDeepFragment;
      eventType?: EventType;
      hasGeometryChange: boolean;
    }
  >();

  // Add reference shapes
  const addReferenceShapes = (shapesData: any) => {
    if (!shapesData) return;

    Object.entries(shapesData).forEach(([urn, shapesList]: [string, any]) => {
      if (Array.isArray(shapesList)) {
        shapesList.forEach((shape: any) => {
          const uniqueId = `${shape.urn}-${shape.id}`;
          shapes.set(uniqueId, {
            shape: fixGeometry(shape) as
              | ShapeDeepFragment
              | SheetShapeDeepFragment,
            hasGeometryChange: false,
          });
        });
      }
    });
  };

  // Process reference state
  addReferenceShapes(referenceState.propertyContext?.loadedShapes);
  addReferenceShapes(referenceState.propertyContext?.loadedSheetShapes);

  // Process diff changes
  // eslint-disable-next-line complexity
  Object.entries(diff).forEach(([key, value]) => {
    const parsed = parseShapeKey(key);
    if (!parsed) {
      // Handle collection-level keys (e.g., propertyContext.loadedShapes.{urn})
      const collectionMatch = key.match(
        /propertyContext\.(loadedShapes|loadedSheetShapes)\.([^.]+)$/
      );
      if (
        collectionMatch &&
        value?.new &&
        typeof value.new === 'object' &&
        !value.new.__typename
      ) {
        // Collection additions (multiple shapes)
        Object.values(value.new).forEach((shape: any) => {
          if (shape && shape.__typename) {
            const shapeUniqueId = `${shape.urn}-${shape.id}`;
            shapes.set(shapeUniqueId, {
              shape: fixGeometry(shape) as
                | ShapeDeepFragment
                | SheetShapeDeepFragment,
              eventType: EventType.Created,
              hasGeometryChange: false,
            });
          }
        });
      }
      // I wonder, is there a scneario where new has a value and old has a value for the collection?
      // or maybe collections do not have this problem, they are either new or old but not both
      else if (
        collectionMatch &&
        value?.old &&
        typeof value.old === 'object' &&
        !value.old.__typename
      ) {
        // Collection additions (multiple shapes)
        Object.values(value.old).forEach((shape: any) => {
          if (shape && shape.__typename) {
            const shapeUniqueId = `${shape.urn}-${shape.id}`;
            shapes.set(shapeUniqueId, {
              shape: fixGeometry(shape) as
                | ShapeDeepFragment
                | SheetShapeDeepFragment,
              eventType: EventType.Deleted,
              hasGeometryChange: false,
            });
          }
        });
      }
      return;
    }

    const { uniqueId } = parsed;
    const isGeometryChange =
      /\.(polygon|extrudedPolygon|shapePoint|line|sheetShapePolygon|sheetShapeLine|sheetShapePoint)\./.test(
        key
      );

    // Handle different types of changes
    if (value?.new && value.new.__typename && !value.old) {
      // Shape creation
      shapes.set(uniqueId, {
        shape: fixGeometry(value.new) as
          | ShapeDeepFragment
          | SheetShapeDeepFragment,
        eventType: EventType.Created,
        hasGeometryChange: false,
      });
    } else if (
      value?.old &&
      (value?.new === null || value?.new === undefined)
    ) {
      // Shape deletion
      const existing = shapes.get(uniqueId);
      if (existing) {
        existing.eventType = EventType.Deleted;
      }
    } else if (
      value?.old !== undefined &&
      value?.new !== undefined &&
      value?.new !== null
    ) {
      // Shape modification
      const existing = shapes.get(uniqueId);
      if (existing) {
        existing.eventType = EventType.Modified;
        if (isGeometryChange) {
          existing.hasGeometryChange = true;
        }
      }
    } else if (
      value?.new &&
      typeof value.new === 'object' &&
      !value.new.__typename
    ) {
      // Collection additions (multiple shapes)
      Object.values(value.new).forEach((shape: any) => {
        if (shape && shape.__typename) {
          const shapeUniqueId = `${shape.urn}-${shape.id}`;
          shapes.set(shapeUniqueId, {
            shape: fixGeometry(shape) as
              | ShapeDeepFragment
              | SheetShapeDeepFragment,
            eventType: EventType.Created,
            hasGeometryChange: false,
          });
        }
      });
    }
  });

  // Check for attribute changes
  shapes.forEach((shapeData, uniqueId) => {
    if (!shapeData.eventType) {
      const [urn, id] = uniqueId.split('-');
      const shape = shapeData.shape as any;
      if (getAttributeDiff(referenceState, diff, urn, shape.dbId)) {
        shapeData.eventType = EventType.Modified;
      }
    }
  });

  // Convert to final result with geometry selection
  const result: ShapeWithEventType[] = [];

  shapes.forEach((shapeData) => {
    // Always show the historical shape state for the selected history point
    // This ensures geometry and attributes reflect that point in time
    const finalShape = shapeData.shape;
    result.push({
      ...finalShape,
      eventType: shapeData.eventType,
    });
  });

  // Apply property modifications
  Object.entries(diff).forEach(([key, value]) => {
    const parsed = parseShapeKey(key);
    if (!parsed || !parsed.propertyPath || value?.new === undefined) return;

    const { urn, id, propertyPath } = parsed;
    const shapeIndex = result.findIndex((s) => s.id === id && s.urn === urn);

    if (shapeIndex !== -1) {
      const shape = { ...result[shapeIndex] } as any;

      // Apply property change
      const keys = propertyPath.split('.');
      let current = shape;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!(key in current) || typeof current[key] !== 'object') {
          current[key] = {};
        }
        current = current[key];
      }

      current[keys[keys.length - 1]] = parseJson(value.new);
      result[shapeIndex] = shape;
    }
  });

  // Ensure friendly property diff for Shape Name is captured from shape changes
  // If a shape's name changed but no BIM property diff exists, surface it via a synthetic diff key
  const nameDiffs: Record<string, { old?: string; new?: string }> = {};
  Object.entries(diff).forEach(([key, value]) => {
    const parsed = parseShapeKey(key);
    if (!parsed) return;
    if (parsed.propertyPath === 'name' && value) {
      const uniqueKey = `${parsed.urn}:${parsed.id}`;
      nameDiffs[uniqueKey] = {
        old: (value as any).old as string | undefined,
        new: (value as any).new as string | undefined,
      };
    }
  });
  // Attach the historical name to the returned shape for later display mapping
  // Consumers (e.g., FriendlyProperties in the panel) can read eventType/name
  if (Object.keys(nameDiffs).length > 0) {
    result.forEach((shape) => {
      const key = `${shape.urn}:${shape.id}`;
      const d = nameDiffs[key];
      if (d && d.new !== undefined) {
        (shape as any).name = d.new;
      }
    });
  }

  return result;
};

// Helper function to parse JSON strings recursively
const parseJson = (value: any): any => {
  if (typeof value === 'string') {
    try {
      return parseJson(JSON.parse(value));
    } catch {
      return value;
    }
  }
  if (Array.isArray(value)) {
    return value.map(parseJson);
  }
  if (value && typeof value === 'object') {
    const result: any = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = parseJson(val);
    }
    return result;
  }
  return value;
};

// Helper function to parse diff key to extract shape folder info
const parseShapeFolderKey = (key: string) => {
  // Match: propertyContext.loadedShapeFolders.{urn}.{id} or propertyContext.loadedShapeFolders.{urn}.{id}.{property}
  const folderMatch = key.match(
    /propertyContext\.loadedShapeFolders\.([^.]+)\.([^.]+)(?:\.(.+))?/
  );
  if (!folderMatch) return null;

  const [, urn, id, propertyPath = ''] = folderMatch;
  return {
    urn,
    id,
    propertyPath,
    uniqueId: `${urn}-${id}`,
  };
};

// Helper function to add reference shape folders
const addReferenceShapeFolders = (
  folders: Map<
    string,
    {
      folder: ShapeFolderDeepFragment;
      eventType?: EventType;
    }
  >,
  foldersData: any
) => {
  if (!foldersData) return;

  Object.entries(foldersData).forEach(([urn, foldersList]: [string, any]) => {
    if (Array.isArray(foldersList)) {
      foldersList.forEach((folder: any) => {
        // Use the URN from the key (project URN) since shape folders don't have their own URN
        const uniqueId = `${urn}-${folder.id}`;
        folders.set(uniqueId, {
          folder: parseJson(folder) as ShapeFolderDeepFragment,
        });
      });
    }
  });
};

// Helper function to handle folder creation
const handleFolderCreation = (
  folders: Map<
    string,
    {
      folder: ShapeFolderDeepFragment;
      eventType?: EventType;
    }
  >,
  uniqueId: string,
  value: any
) => {
  folders.set(uniqueId, {
    folder: parseJson(value.new) as ShapeFolderDeepFragment,
    eventType: EventType.Created,
  });
};

// Helper function to handle folder deletion
const handleFolderDeletion = (
  folders: Map<
    string,
    {
      folder: ShapeFolderDeepFragment;
      eventType?: EventType;
    }
  >,
  uniqueId: string
) => {
  const existing = folders.get(uniqueId);
  if (existing) {
    existing.eventType = EventType.Deleted;
  }
};

// Helper function to handle folder modification
const handleFolderModification = (
  folders: Map<
    string,
    {
      folder: ShapeFolderDeepFragment;
      eventType?: EventType;
    }
  >,
  uniqueId: string
) => {
  const existing = folders.get(uniqueId);
  if (existing) {
    existing.eventType = EventType.Modified;
  }
};

// Helper function to process diff changes for folders
const processFolderDiffChanges = (
  folders: Map<
    string,
    {
      folder: ShapeFolderDeepFragment;
      eventType?: EventType;
    }
  >,
  diff: Record<string, any>
) => {
  // eslint-disable-next-line complexity
  Object.entries(diff).forEach(([key, value]) => {
    const parsed = parseShapeFolderKey(key);
    if (!parsed) {
      // Handle collection replacement case (when entire folders collection is replaced)
      const folderCollectionMatch = key.match(
        /propertyContext\.loadedShapeFolders\.([^.]+)/
      );
      if (
        folderCollectionMatch &&
        value?.new &&
        typeof value.new === 'object'
      ) {
        const [, urn] = folderCollectionMatch;
        // Process each folder in the collection
        Object.entries(value.new).forEach(
          ([folderId, folderData]: [string, any]) => {
            if (folderData && folderData.id) {
              const folderUniqueId = `${urn}-${folderId}`;
              folders.set(folderUniqueId, {
                folder: parseJson(folderData) as ShapeFolderDeepFragment,
                eventType: EventType.Created,
              });
            }
          }
        );
      }
      return;
    }

    const { uniqueId } = parsed;

    // Handle different types of changes
    if (value?.new && value.new.id && !value.old) {
      // Folder creation - check for id property instead of __typename
      handleFolderCreation(folders, uniqueId, value);
    } else if (
      value?.old &&
      (value?.new === null || value?.new === undefined)
    ) {
      handleFolderDeletion(folders, uniqueId);
    } else if (
      value?.old !== undefined &&
      value?.new !== undefined &&
      value?.new !== null
    ) {
      handleFolderModification(folders, uniqueId);
    } else if (
      value?.new &&
      typeof value.new === 'object' &&
      !value.new.id &&
      Array.isArray(value.new)
    ) {
      // Handle collection additions (multiple folders added at once)
      value.new.forEach((folder: any) => {
        if (folder && folder.id) {
          const folderUniqueId = `${parsed.urn}-${folder.id}`;
          folders.set(folderUniqueId, {
            folder: parseJson(folder) as ShapeFolderDeepFragment,
            eventType: EventType.Created,
          });
        }
      });
    }
  });
};

// Helper function to apply property modifications to folders
const applyFolderPropertyModifications = (
  result: (ShapeFolderDeepFragment & { eventType?: EventType })[],
  diff: Record<string, any>
) => {
  Object.entries(diff).forEach(([key, value]) => {
    const parsed = parseShapeFolderKey(key);
    if (!parsed || !parsed.propertyPath || value?.new === undefined) return;

    const { urn, id, propertyPath } = parsed;
    const folderIndex = result.findIndex((f) => f.id === id);

    if (folderIndex !== -1) {
      const folder = { ...result[folderIndex] } as any;

      // Apply property change
      const keys = propertyPath.split('.');
      let current = folder;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!(key in current) || typeof current[key] !== 'object') {
          current[key] = {};
        }
        current = current[key];
      }

      current[keys[keys.length - 1]] = parseJson(value.new);
      result[folderIndex] = folder;
    }
  });
};

export const getHistoricalShapeFolders = (
  referenceState: any,
  diff: Record<string, any>,
  currentShapeFolders: ShapeFolderDeepFragment[] | undefined
): (ShapeFolderDeepFragment & { eventType?: EventType })[] => {
  // Collect all shape folders with their change status
  const folders = new Map<
    string,
    {
      folder: ShapeFolderDeepFragment;
      eventType?: EventType;
    }
  >();

  // Process reference state
  addReferenceShapeFolders(
    folders,
    referenceState.propertyContext?.loadedShapeFolders
  );

  // Process diff changes
  processFolderDiffChanges(folders, diff);

  // Convert to final result
  const result: (ShapeFolderDeepFragment & { eventType?: EventType })[] = [];

  folders.forEach((folderData) => {
    let finalFolder = folderData.folder;

    // Use current database folder for modifications
    if (folderData.eventType === EventType.Modified) {
      const currentFolder = currentShapeFolders?.find(
        (f) => f.id === folderData.folder.id
      );
      if (currentFolder) {
        finalFolder = currentFolder;
      }
    }

    result.push({
      ...finalFolder,
      eventType: folderData.eventType,
    });
  });

  // Apply property modifications
  applyFolderPropertyModifications(result, diff);

  return result;
};

export const getHistoricalOrders = (
  referenceState: any,
  diff: Record<string, any>
): OrderDeepFragment[] => {
  const ordersMap = new Map<string, OrderDeepFragment>();

  const addOrders = (orders: any) => {
    if (!orders) return;
    if (Array.isArray(orders)) {
      orders.forEach((o: any) => {
        if (o && o.id) ordersMap.set(o.id, o as OrderDeepFragment);
      });
    } else if (typeof orders === 'object') {
      Object.values(orders).forEach((o: any) => {
        if (o && o.id) ordersMap.set(o.id, o as OrderDeepFragment);
      });
    }
  };

  addOrders(referenceState?.project?.orders);

  const collectionDiff = diff?.['project.orders'];
  if (collectionDiff) {
    addOrders(collectionDiff.old);
    addOrders(collectionDiff.new);
  }

  // Also handle per-order diff keys like: project.orders.<orderId>
  // Only match exact order-level keys (exclude nested props like orderColumns)
  Object.entries(diff).forEach(([key, value]) => {
    const match = key.match(/^project\.orders\.([^.]+)$/);
    if (!match) return;

    const orderCandidate = value?.new;
    if (
      orderCandidate &&
      typeof orderCandidate === 'object' &&
      orderCandidate.id
    ) {
      ordersMap.set(
        orderCandidate.id,
        parseJson(orderCandidate) as OrderDeepFragment
      );
    }
  });

  return Array.from(ordersMap.values());
};

// Build historical field diffs for a specific order
export const getHistoricalOrderFields = (
  resolvedStates: [any, any],
  diff: Record<string, any>,
  orderId: string
): {
  name: { old?: string; new?: string; changed: boolean };
  status: { old?: string; new?: string; changed: boolean };
  labels: { old?: string; new?: string; changed: boolean };
} => {
  const [referenceState, newerState] = resolvedStates;

  const oldOrder: any = referenceState?.project?.orders?.find(
    (order: OrderDeepFragment) => order.id === orderId
  );
  const newOrder: any = newerState?.project?.orders?.find(
    (order: OrderDeepFragment) => order.id === orderId
  );

  let orderNew = newOrder;
  if (!orderNew) {
    orderNew = oldOrder;
  }

  const orderKey = `project.orders.${orderId}`;
  const orderObj = diff?.[orderKey] as
    | { old?: Record<string, unknown>; new?: Record<string, unknown> }
    | undefined;

  const result = {
    name: { changed: false } as {
      old?: string;
      new?: string;
      changed: boolean;
    },
    status: { changed: false } as {
      old?: string;
      new?: string;
      changed: boolean;
    },
    labels: { changed: false } as {
      old?: string;
      new?: string;
      changed: boolean;
    },
  };

  // eslint-disable-next-line complexity
  const setField = (field: 'name' | 'status' | 'labels') => {
    // Prefer direct field diff
    const direct = diff?.[`project.orders.${orderId}.${field}`] as
      | { old?: unknown; new?: unknown }
      | undefined;
    if (direct && (direct.old !== undefined || direct.new !== undefined)) {
      (result as any)[field] = {
        old: direct.old !== undefined ? String(direct.old) : undefined,
        new:
          direct.new !== undefined
            ? String(direct.new)
            : orderNew?.[field] !== undefined
            ? String(orderNew[field])
            : undefined,
        changed: true,
      };
      return;
    }

    // Whole-order diff
    if (orderObj && (orderObj.old || orderObj.new)) {
      const oldVal = orderObj.old?.[field];
      const newVal = orderObj.new?.[field];
      if (oldVal !== undefined || newVal !== undefined) {
        (result as any)[field] = {
          old: oldVal !== undefined ? String(oldVal) : undefined,
          new:
            newVal !== undefined
              ? String(newVal)
              : orderNew?.[field] !== undefined
              ? String(orderNew[field])
              : undefined,
          changed: true,
        };
        return;
      }
    }

    // Collection-level diff
    if (oldOrder || newOrder) {
      const oldVal = oldOrder?.[field];
      const newVal = newOrder?.[field];
      (result as any)[field] = {
        old: oldVal !== undefined ? String(oldVal) : undefined,
        new:
          newVal !== undefined
            ? String(newVal)
            : orderNew?.[field] !== undefined
            ? String(orderNew[field])
            : undefined,
        changed: oldVal !== newVal,
      };
      return;
    }

    // No diff entry: set both to the historical value to avoid marking as created
    const histVal = orderNew?.[field];
    (result as any)[field] = {
      old: histVal !== undefined ? String(histVal) : undefined,
      new: histVal !== undefined ? String(histVal) : undefined,
      changed: false,
    };
  };

  setField('name');
  setField('status');
  setField('labels');

  return result;
};

// Build per-label diffs for an order by constructing historical old/new states
// eslint-disable-next-line complexity
export const getHistoricalOrderLabelDiffs = (
  resolvedStates: [any, any],
  orderId: string
): Array<{ key: string; old?: string; new?: string }> => {
  const [referenceState, newerState] = resolvedStates;
  const oldOrder: any = referenceState?.project?.orders?.find(
    (order: OrderDeepFragment) => order.id === orderId
  );
  const newOrder: any = newerState?.project?.orders?.find(
    (order: OrderDeepFragment) => order.id === orderId
  );
  const oldLabels: Array<{ key: string; value: string }> =
    oldOrder?.labels ?? [];
  const newLabels: Array<{ key: string; value: string }> =
    newOrder?.labels ?? [];

  const keySet = new Set<string>([
    ...oldLabels.map((l) => l.key),
    ...newLabels.map((l) => l.key),
  ]);

  const oldMap = new Map(oldLabels.map((l) => [l.key, l.value]));
  const newMap = new Map(newLabels.map((l) => [l.key, l.value]));

  const result: Array<{ key: string; old?: string; new?: string }> = [];
  keySet.forEach((k) => {
    const o = oldMap.get(k);
    const n = newMap.get(k);
    // Only include if present in at least one side
    if (o !== undefined || n !== undefined) {
      result.push({ key: k, old: o, new: n });
    }
  });

  // Sort for stable display
  result.sort((a, b) => a.key.localeCompare(b.key));
  return result;
};

export interface DiffColors {
  [EventType.Modified]: string;
  [EventType.Created]: string;
  [EventType.Deleted]: string;
  [EventType.Duplicated]: string;
}

export const useDiffing = () => {
  const viewerContext = useContext(ForgeViewerContext);
  const viewerLoadedModels = viewerContext?.loadedModels ?? ({} as ModelsMap);
  const textColors: DiffColors = {
    [EventType.Modified]: useColorModeValue(
      ChakraColor.orange800,
      ChakraColor.orange200
    ),
    [EventType.Created]: useColorModeValue(
      ChakraColor.green800,
      ChakraColor.green200
    ),
    [EventType.Deleted]: useColorModeValue(
      ChakraColor.red800,
      ChakraColor.red200
    ),
    [EventType.Duplicated]: useColorModeValue(
      ChakraColor.gray800,
      ChakraColor.gray200
    ),
  };

  const backgroundColors: DiffColors = {
    [EventType.Modified]: useColorModeValue(
      ChakraColor.orange50,
      `${ChakraColor.orange200}29`
    ),
    [EventType.Created]: useColorModeValue(
      ChakraColor.green50,
      `${ChakraColor.green200}29`
    ),
    [EventType.Deleted]: useColorModeValue(
      ChakraColor.red50,
      `${ChakraColor.red200}29`
    ),
    [EventType.Duplicated]: useColorModeValue(
      ChakraColor.gray50,
      `${ChakraColor.gray200}29`
    ),
  };

  const getTextColor = (eventType: EventType): string => {
    return textColors[eventType];
  };

  const getBackgroundColor = (eventType: EventType): string => {
    return backgroundColors[eventType];
  };

  const getEventType = (diff: {
    new?: string;
    old?: string;
  }): EventType | undefined => {
    const hasNew = diff.new !== undefined;
    const hasOld = diff.old !== undefined;

    if (hasNew && hasOld) {
      return diff.new !== diff.old ? EventType.Modified : undefined;
    }

    if (hasNew) return EventType.Created;
    if (hasOld) return EventType.Deleted;

    return undefined;
  };

  // Get historical attribute diff for a specific category and element
  const getHistoricalAttributes = (
    referenceState: any,
    diff: Record<string, any>,
    urn: string,
    dbId: number,
    category: string
  ): { old?: string; new?: string } | undefined => {
    if (!referenceState || !diff) {
      return undefined;
    }

    // Fast path: check property value changes first (most common case)
    const fastPathResult = checkPropertyValueChanges(
      referenceState,
      diff,
      urn,
      dbId,
      category
    );
    if (fastPathResult) {
      return fastPathResult;
    }

    // Slow path: check collection and object level changes
    return checkCollectionAndObjectChanges(
      referenceState,
      diff,
      urn,
      dbId,
      category
    );
  };

  // Get the historical value for a specific attribute category from referenceState
  // This is used when there's no diff but we want to show the historical value
  const getHistoricalAttributeValue = (
    referenceState: any,
    urn: string,
    dbId: number,
    category: string
  ): string | undefined => {
    if (!referenceState) return undefined;

    const properties =
      referenceState?.propertyContext?.loadedSparkelProperties?.[urn];

    if (!Array.isArray(properties)) return undefined;

    // Find the property that matches both dbId and category
    const property = properties.find(
      (prop: any) => prop?.dbId === dbId && prop?.propertySet === category
    );

    return property?.propertyValue || undefined;
  };

  // Fast path: check property value changes (same logic as getAttributeDiff)
  const checkPropertyValueChanges = (
    referenceState: any,
    diff: Record<string, any>,
    urn: string,
    dbId: number,
    category: string
  ): { old?: string; new?: string } | undefined => {
    const propertyPattern = `propertyContext.loadedSparkelProperties.${urn}.`;
    const propertyDiffKeys = Object.keys(diff).filter(
      (key) => key.startsWith(propertyPattern) && key.endsWith('.propertyValue')
    );

    for (const diffKey of propertyDiffKeys) {
      const propertyId = diffKey
        .replace(propertyPattern, '')
        .replace('.propertyValue', '');

      const properties =
        referenceState?.propertyContext?.loadedSparkelProperties?.[urn];
      if (properties && Array.isArray(properties)) {
        const property = properties.find(
          (prop: any) =>
            prop.id === propertyId &&
            prop.dbId === dbId &&
            prop.propertySet === category
        );

        if (property) {
          const propertyDiff = diff[diffKey];
          if (
            propertyDiff &&
            (propertyDiff.old !== undefined || propertyDiff.new !== undefined)
          ) {
            return {
              old: propertyDiff.old,
              new: propertyDiff.new,
            };
          }
        }
      }
    }
    return undefined;
  };

  // Slow path: check collection and object level changes
  const checkCollectionAndObjectChanges = (
    referenceState: any,
    diff: Record<string, any>,
    urn: string,
    dbId: number,
    category: string
  ): { old?: string; new?: string } | undefined => {
    const baseKey = `propertyContext.loadedSparkelProperties.${urn}`;

    let oldVal: string | undefined;
    let newVal: string | undefined;

    type SparkelProp = {
      id: string;
      projectId: string;
      propertySet: string;
      propertyValue?: string | null;
      modelUrn: string;
      dbId: number;
    };

    const isMatchingProp = (p: unknown): p is SparkelProp => {
      if (!p || typeof p !== 'object') return false;
      const obj = p as Record<string, unknown>;
      return (
        typeof obj['id'] === 'string' &&
        typeof obj['dbId'] === 'number' &&
        obj['dbId'] === dbId &&
        typeof obj['propertySet'] === 'string' &&
        (obj['propertySet'] as string) === category
      );
    };

    const scanCollectionLevel = (v: { old?: unknown; new?: unknown }) => {
      const oldObj = (v.old as Record<string, unknown>) || {};
      const newObj = (v.new as Record<string, unknown>) || {};
      Object.values(oldObj).forEach((p) => {
        if (isMatchingProp(p))
          oldVal = (p as SparkelProp).propertyValue ?? undefined;
      });
      Object.values(newObj).forEach((p) => {
        if (isMatchingProp(p))
          newVal = (p as SparkelProp).propertyValue ?? undefined;
      });
    };

    const scanObjectLevelChange = (
      key: string,
      v: { old?: unknown; new?: unknown }
    ) => {
      const mObj = key.match(
        /^propertyContext\.loadedSparkelProperties\.([^.]+)\.([^.]+)$/
      );
      if (!mObj || mObj[1] !== urn) return;
      const maybeOld = v.old as unknown;
      const maybeNew = v.new as unknown;
      if (isMatchingProp(maybeOld))
        oldVal = (maybeOld as SparkelProp).propertyValue ?? undefined;
      if (isMatchingProp(maybeNew))
        newVal = (maybeNew as SparkelProp).propertyValue ?? undefined;
    };

    Object.entries(diff).forEach(([key, value]) => {
      const v = value as { old?: unknown; new?: unknown } | undefined;
      if (!v) return;
      if (key === baseKey) {
        scanCollectionLevel(v);
        return;
      }
      scanObjectLevelChange(key, v);
    });

    if (oldVal === undefined && newVal === undefined) return undefined;
    return { old: oldVal, new: newVal };
  };

  return {
    getTextColor,
    getBackgroundColor,
    getEventType,
    getAttributeDiff,
    getHistoricalAttributes,
    getHistoricalAttributeValue,
    // Build old/new property contexts entirely from reference + diff
    getHistoricalContexts: (
      referenceState: any,
      diff: Record<string, any>
    ): {
      oldCtx?: HistoricalPropertyContext;
      newCtx?: HistoricalPropertyContext;
    } => {
      // The stored context is not a real PropertyContext (it is serialized as URN[])
      const refCtx = referenceState?.propertyContext as
        | HistoricalPropertyContext
        | undefined;
      if (!refCtx) return {};
      const oldCtx: HistoricalPropertyContext = cloneDeep(refCtx);
      const newCtx: HistoricalPropertyContext = cloneDeep(refCtx);

      const applyShapePath = (
        root: any,
        collection: 'loadedShapes' | 'loadedSheetShapes',
        urn: string,
        shapeId: string,
        propertyPath: string,
        val: unknown
      ) => {
        if (val === undefined) return;
        const shapesArr = root?.[collection]?.[urn];
        if (!Array.isArray(shapesArr)) return;
        const idx = shapesArr.findIndex((s: any) => s?.id === shapeId);
        if (idx === -1) return;
        let cursor: any = shapesArr[idx];
        const parts = propertyPath.split('.');
        for (let i = 0; i < parts.length - 1; i++) {
          const seg = parts[i];
          if (cursor[seg] === undefined || typeof cursor[seg] !== 'object') {
            cursor[seg] = {};
          }
          cursor = cursor[seg];
        }
        cursor[parts[parts.length - 1]] = parseJson(val);
      };

      const applyGenericPath = (root: any, innerKey: string, val: unknown) => {
        if (val === undefined) return;
        const path = innerKey.split('.');
        let cursor = root;
        for (let i = 0; i < path.length - 1; i++) {
          const seg = path[i];
          if (cursor[seg] === undefined || typeof cursor[seg] !== 'object') {
            cursor[seg] = {};
          }
          cursor = cursor[seg];
        }
        cursor[path[path.length - 1]] = parseJson(val);
      };

      Object.entries(diff).forEach(([key, value]) => {
        if (!key.startsWith('propertyContext.')) return;
        const innerKey = key.replace(/^propertyContext\./, '');
        const m = innerKey.match(
          /^(loadedShapes|loadedSheetShapes)\.([^.]+)\.([^.]+)\.(.+)$/
        );
        if (m) {
          const collection = m[1] as 'loadedShapes' | 'loadedSheetShapes';
          const urn = m[2];
          const shapeId = m[3];
          const propertyPath = m[4];
          applyShapePath(
            oldCtx as any,
            collection,
            urn,
            shapeId,
            propertyPath,
            value?.old
          );
          applyShapePath(
            newCtx as any,
            collection,
            urn,
            shapeId,
            propertyPath,
            value?.new
          );
          return;
        }
        // Handle collection-level keys (e.g., loadedShapes.<urn>) where value is an object
        const mCollection = innerKey.match(
          /^(loadedShapes|loadedSheetShapes)\.([^.]+)$/
        );
        if (mCollection) {
          const collection = mCollection[1] as
            | 'loadedShapes'
            | 'loadedSheetShapes';
          const urn = mCollection[2];
          const addShapesFromCollection = (root: any, v: unknown) => {
            if (!v || typeof v !== 'object') return;
            if (!root[collection]) root[collection] = {};
            if (!Array.isArray(root[collection][urn]))
              root[collection][urn] = [];
            const arr = root[collection][urn] as any[];
            Object.values(v as Record<string, unknown>).forEach(
              (shape: any) => {
                if (!shape || typeof shape !== 'object') return;
                const parsed = parseJson(shape) as any;
                const id = (parsed && parsed.id) as string | undefined;
                if (!id) return;
                const exists = arr.some((s) => s?.id === id);
                if (!exists) arr.push(parsed);
              }
            );
          };
          if (value?.old) addShapesFromCollection(oldCtx as any, value.old);
          if (value?.new) addShapesFromCollection(newCtx as any, value.new);
          return;
        }
        // Handle shape creation keys without propertyPath: loadedShapes.<urn>.<id>
        const mCreate = innerKey.match(
          /^(loadedShapes|loadedSheetShapes)\.([^.]+)\.([^.]+)$/
        );
        if (mCreate) {
          const collection = mCreate[1] as 'loadedShapes' | 'loadedSheetShapes';
          const urn = mCreate[2];
          const shapeId = mCreate[3];
          const addShape = (root: any, v: unknown) => {
            if (!v || typeof v !== 'object') return;
            if (!root[collection]) root[collection] = {};
            if (!Array.isArray(root[collection][urn]))
              root[collection][urn] = [];
            const arr = root[collection][urn] as any[];
            const exists = arr.some((s) => s?.id === shapeId);
            if (!exists) arr.push(parseJson(v));
          };
          if (value?.old) addShape(oldCtx as any, value.old);
          if (value?.new) addShape(newCtx as any, value.new);
          return;
        }
        const mFolder = innerKey.match(
          /^(loadedShapeFolders)\.([^.]+)\.([^.]+)(?:\.(.+))?$/
        );
        if (mFolder) {
          const urn = mFolder[2];
          const folderId = mFolder[3];
          const propertyPath = mFolder[4];
          // eslint-disable-next-line complexity
          const applyFolderPath = (
            root: any,
            urnKey: string,
            id: string,
            propPath: string | undefined,
            v: unknown
          ) => {
            if (v === undefined) return;
            if (!root.loadedShapeFolders) root.loadedShapeFolders = {};
            if (!Array.isArray(root.loadedShapeFolders[urnKey])) {
              root.loadedShapeFolders[urnKey] = Array.isArray(
                root.loadedShapeFolders[urnKey]
              )
                ? root.loadedShapeFolders[urnKey]
                : [];
            }
            const arr = root.loadedShapeFolders[urnKey] as any[];
            let idx = arr.findIndex((f) => f?.id === id);
            if (idx === -1) {
              const newObj = typeof v === 'object' ? parseJson(v) : { id };
              arr.push(newObj);
              idx = arr.length - 1;
            }
            if (!propPath) {
              arr[idx] = parseJson(v);
              return;
            }
            let cursor: any = arr[idx];
            const parts = propPath.split('.');
            for (let i = 0; i < parts.length - 1; i++) {
              const seg = parts[i];
              if (
                cursor[seg] === undefined ||
                typeof cursor[seg] !== 'object'
              ) {
                cursor[seg] = {};
              }
              cursor = cursor[seg];
            }
            cursor[parts[parts.length - 1]] = parseJson(v);
          };
          applyFolderPath(
            oldCtx as any,
            urn,
            folderId,
            propertyPath,
            value?.old
          );
          applyFolderPath(
            newCtx as any,
            urn,
            folderId,
            propertyPath,
            value?.new
          );
          return;
        }
        applyGenericPath(oldCtx as any, innerKey, value?.old);
        applyGenericPath(newCtx as any, innerKey, value?.new);
      });

      oldCtx.loadedModels = [];
      newCtx.loadedModels = [];
      return { oldCtx, newCtx };
    },
    // eslint-disable-next-line complexity
    getHistoricalProperties: async (
      referenceState: any,
      diff: Record<string, any>,
      selectedDbIds: DbIdsPerModel,
      settings: {
        polygonCalculationLimit?: number;
        viewerGlobalOffset?: { x?: number; y?: number; z?: number };
      },
      precision = 2
    ): Promise<Record<string, { old?: unknown; new?: unknown }>> => {
      // Only support single-element selection
      const selectedCount = Object.values(selectedDbIds).flat().length;
      if (selectedCount !== 1) return {};

      // Build contexts
      // The stored context is not the actual PropertyContext (e.g., loadedModels is serialized as URN[])
      const refCtx = referenceState?.propertyContext as
        | HistoricalPropertyContext
        | undefined;
      if (!refCtx) return {};
      // Construct old/new contexts entirely from reference, then apply diff-old/diff-new
      const oldCtxRaw: HistoricalPropertyContext = cloneDeep(refCtx);
      const newCtxRaw: HistoricalPropertyContext = cloneDeep(refCtx);

      const oldCtx: PropertyContext = {
        ...oldCtxRaw,
        loadedModels: {} as ModelsMap,
      };
      const newCtx: PropertyContext = {
        ...newCtxRaw,
        loadedModels: {} as ModelsMap,
      };

      // Determine selected element early for debugging
      const [modelUrn, ids] = Object.entries(selectedDbIds)[0] ?? [];
      const dbId = Array.isArray(ids) ? ids[0] : undefined;
      if (!modelUrn || dbId === undefined) return {};

      // (Removed noisy pre-apply identity debug)

      // JSON parser
      const parseJson = (val: unknown): unknown => {
        if (typeof val === 'string') {
          try {
            return parseJson(JSON.parse(val));
          } catch {
            return val;
          }
        }
        if (Array.isArray(val)) return val.map(parseJson);
        if (val && typeof val === 'object') {
          const out: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(val)) out[k] = parseJson(v);
          return out;
        }
        return val;
      };

      // Apply ALL propertyContext diffs to both contexts: oldCtx<-old, newCtx<-new
      Object.entries(diff).forEach(([key, value]) => {
        if (!key.startsWith('propertyContext.')) return;
        const innerKey = key.replace(/^propertyContext\./, '');

        const applyShapePath = (
          root: any,
          collection: 'loadedShapes' | 'loadedSheetShapes',
          urn: string,
          shapeId: string,
          propertyPath: string,
          val: unknown
        ) => {
          if (val === undefined) return;
          if (!root[collection]) root[collection] = {};
          if (!Array.isArray(root[collection][urn])) root[collection][urn] = [];
          const arr = root[collection][urn] as any[];
          let idx = arr.findIndex((s: any) => s?.id === shapeId);
          if (idx === -1) {
            // Create a minimal stub for missing shape (e.g., created in diff)
            const stub: any = { id: shapeId, urn };
            // dbId available from outer scope
            stub.dbId = dbId;
            arr.push(stub);
            idx = arr.length - 1;
          }
          let cursor: any = arr[idx];
          const parts = propertyPath.split('.');
          for (let i = 0; i < parts.length - 1; i++) {
            const seg = parts[i];
            if (cursor[seg] === undefined || typeof cursor[seg] !== 'object') {
              cursor[seg] = {};
            }
            cursor = cursor[seg];
          }
          cursor[parts[parts.length - 1]] = parseJson(val);
        };

        const applyGenericPath = (root: any, val: unknown) => {
          if (val === undefined) return;
          const path = innerKey.split('.');
          let cursor = root;
          for (let i = 0; i < path.length - 1; i++) {
            const seg = path[i];
            if (cursor[seg] === undefined || typeof cursor[seg] !== 'object') {
              cursor[seg] = {};
            }
            cursor = cursor[seg];
          }
          cursor[path[path.length - 1]] = parseJson(val);
        };

        // Shapes arrays need special handling to locate by id instead of treating array as an object
        const m = innerKey.match(
          /^(loadedShapes|loadedSheetShapes)\.([^.]+)\.([^.]+)\.(.+)$/
        );
        if (m) {
          const collection = m[1] as 'loadedShapes' | 'loadedSheetShapes';
          const urn = m[2];
          const shapeId = m[3];
          const propertyPath = m[4];
          applyShapePath(
            oldCtx as any,
            collection,
            urn,
            shapeId,
            propertyPath,
            value?.old
          );
          applyShapePath(
            newCtx as any,
            collection,
            urn,
            shapeId,
            propertyPath,
            value?.new
          );
        } else {
          // Handle collection-level keys (e.g., loadedShapes.<urn>) where value is an object
          const mCollection = innerKey.match(
            /^(loadedShapes|loadedSheetShapes)\.([^.]+)$/
          );
          if (mCollection) {
            const collection = mCollection[1] as
              | 'loadedShapes'
              | 'loadedSheetShapes';
            const urn = mCollection[2];
            const addShapesFromCollection = (root: any, v: unknown) => {
              if (!v || typeof v !== 'object') return;
              if (!root[collection]) root[collection] = {};
              if (!Array.isArray(root[collection][urn]))
                root[collection][urn] = [];
              const arr = root[collection][urn] as any[];
              Object.values(v as Record<string, unknown>).forEach(
                (shape: any) => {
                  if (!shape || typeof shape !== 'object') return;
                  const parsed = parseJson(shape) as any;
                  const id = (parsed && parsed.id) as string | undefined;
                  if (!id) return;
                  const exists = arr.some((s) => s?.id === id);
                  if (!exists) arr.push(parsed);
                }
              );
            };
            if (value?.old) addShapesFromCollection(oldCtx as any, value.old);
            if (value?.new) addShapesFromCollection(newCtx as any, value.new);
            return;
          }
          // Handle creation keys without propertyPath: loadedShapes.<urn>.<id>
          const mCreate = innerKey.match(
            /^(loadedShapes|loadedSheetShapes)\.([^.]+)\.([^.]+)$/
          );
          if (mCreate) {
            const collection = mCreate[1] as
              | 'loadedShapes'
              | 'loadedSheetShapes';
            const urn = mCreate[2];
            const shapeId = mCreate[3];
            const addShape = (root: any, v: unknown) => {
              if (!v || typeof v !== 'object') return;
              if (!root[collection]) root[collection] = {};
              if (!Array.isArray(root[collection][urn]))
                root[collection][urn] = [];
              const arr = root[collection][urn] as any[];
              const exists = arr.some((s) => s?.id === shapeId);
              if (!exists) arr.push(parseJson(v));
            };
            if (value?.old) addShape(oldCtx as any, value.old);
            if (value?.new) addShape(newCtx as any, value.new);
            return;
          }
          // Handle shape folders which are arrays keyed by urn, with items found by id
          const mFolder = innerKey.match(
            /^(loadedShapeFolders)\.([^.]+)\.([^.]+)(?:\.(.+))?$/
          );
          if (mFolder) {
            const urn = mFolder[2];
            const folderId = mFolder[3];
            const propertyPath = mFolder[4]; // can be undefined (whole folder)

            // eslint-disable-next-line complexity
            const applyFolderPath = (
              root: any,
              urnKey: string,
              id: string,
              propPath: string | undefined,
              v: unknown
            ) => {
              if (v === undefined) return;
              if (!root.loadedShapeFolders) root.loadedShapeFolders = {};
              if (!Array.isArray(root.loadedShapeFolders[urnKey])) {
                root.loadedShapeFolders[urnKey] = Array.isArray(
                  root.loadedShapeFolders[urnKey]
                )
                  ? root.loadedShapeFolders[urnKey]
                  : [];
              }
              const arr = root.loadedShapeFolders[urnKey] as any[];
              let idx = arr.findIndex((f) => f?.id === id);
              if (idx === -1) {
                // Create if full object provided
                const newObj = typeof v === 'object' ? parseJson(v) : { id };
                arr.push(newObj);
                idx = arr.length - 1;
              }
              if (!propPath) {
                // Replace whole folder object
                arr[idx] = parseJson(v);
                return;
              }
              let cursor: any = arr[idx];
              const parts = propPath.split('.');
              for (let i = 0; i < parts.length - 1; i++) {
                const seg = parts[i];
                if (
                  cursor[seg] === undefined ||
                  typeof cursor[seg] !== 'object'
                ) {
                  cursor[seg] = {};
                }
                cursor = cursor[seg];
              }
              cursor[parts[parts.length - 1]] = parseJson(v);
            };

            applyFolderPath(
              oldCtx as any,
              urn,
              folderId,
              propertyPath,
              value?.old
            );
            applyFolderPath(
              newCtx as any,
              urn,
              folderId,
              propertyPath,
              value?.new
            );
          } else {
            applyGenericPath(oldCtx as any, value?.old);
            applyGenericPath(newCtx as any, value?.new);
          }
        }
      });

      const cleanedSettings = {
        maxTrianglesForComplexAlgorithm:
          settings?.polygonCalculationLimit || 5000,
        globalOffset: [
          settings?.viewerGlobalOffset?.x || 0,
          settings?.viewerGlobalOffset?.y || 0,
          settings?.viewerGlobalOffset?.z || 0,
        ] as Point,
      };

      const elementDbIds = convertToElementIdentifierDbidType({
        [modelUrn]: [dbId],
      });

      // Hydrate loadedModels from the live viewer for required URNs
      const hydrateModelsForUrns = (urns: string[]): ModelsMap => {
        const map: ModelsMap = {};
        urns.forEach((u) => {
          const m = viewerLoadedModels[u];
          if (m) map[u] = m;
        });
        return map;
      };

      const requiredUrns = [modelUrn];
      const hydratedModels = hydrateModelsForUrns(requiredUrns);
      const canResolveModel = Boolean(hydratedModels[modelUrn]);
      if (canResolveModel) {
        oldCtx.loadedModels = hydratedModels;
        newCtx.loadedModels = hydratedModels;
      }
      // (Removed shapes before/after debug)

      const diffLog: Record<string, { old?: unknown; new?: unknown }> = {};

      // Resolve properties (includes inherent + sparkel + model + shape) if possible
      if (canResolveModel) {
        const [refProps, postProps] = await Promise.all([
          resolveProperties(oldCtx, cleanedSettings, undefined, elementDbIds),
          resolveProperties(newCtx, cleanedSettings, undefined, elementDbIds),
        ]);
        const keyFor = (p: {
          attribute: { category: string; name: string };
          value: unknown;
        }) => `${p.attribute.category}:${p.attribute.name}`;
        const refMap = new Map(refProps.map((p) => [keyFor(p), p]));
        const postMap = new Map(postProps.map((p) => [keyFor(p), p]));
        const allKeys = new Set<string>([...refMap.keys(), ...postMap.keys()]);
        allKeys.forEach((k) => {
          const r = (refMap.get(k) as { value?: unknown } | undefined)?.value;
          const n = (postMap.get(k) as { value?: unknown } | undefined)?.value;
          if (r !== n) diffLog[k] = { old: r, new: n };
        });
      }

      // Determine if selection is a Shape/SheetShape (can compute geometry without models)
      const isShapeSelection = (() => {
        const has3D = Array.isArray((oldCtx as any)?.loadedShapes?.[modelUrn])
          ? (oldCtx as any).loadedShapes[modelUrn].some(
              (s: any) => s?.dbId === dbId
            )
          : Array.isArray((newCtx as any)?.loadedShapes?.[modelUrn])
          ? (newCtx as any).loadedShapes[modelUrn].some(
              (s: any) => s?.dbId === dbId
            )
          : false;
        const has2D = Array.isArray(
          (oldCtx as any)?.loadedSheetShapes?.[modelUrn]
        )
          ? (oldCtx as any).loadedSheetShapes[modelUrn].some(
              (s: any) => s?.dbId === dbId
            )
          : Array.isArray((newCtx as any)?.loadedSheetShapes?.[modelUrn])
          ? (newCtx as any).loadedSheetShapes[modelUrn].some(
              (s: any) => s?.dbId === dbId
            )
          : false;
        return has3D || has2D;
      })();

      // Ensure Shape:Folder diff exists even if resolveProperties omitted it
      const findShapeByDbId = (ctx: PropertyContext): any | undefined => {
        const arr3d = (ctx as any)?.loadedShapes?.[modelUrn];
        if (Array.isArray(arr3d)) {
          const hit = arr3d.find((s: any) => s?.dbId === dbId);
          if (hit) return hit;
        }
        const arr2d = (ctx as any)?.loadedSheetShapes?.[modelUrn];
        if (Array.isArray(arr2d)) {
          const hit = arr2d.find((s: any) => s?.dbId === dbId);
          if (hit) return hit;
        }
        return undefined;
      };
      const sOld = findShapeByDbId(oldCtx);
      const sNew = findShapeByDbId(newCtx);
      const folderNameFromCtx = (ctx: PropertyContext, folderId?: string) => {
        if (!folderId) return undefined;
        const folders = (ctx as any)?.loadedShapeFolders?.[modelUrn];
        if (!Array.isArray(folders)) return undefined;
        const f = folders.find((ff: any) => ff?.id === folderId);
        return f?.name as string | undefined;
      };
      const oldFolderName = folderNameFromCtx(oldCtx, sOld?.folder);
      const newFolderName = folderNameFromCtx(newCtx, sNew?.folder);
      if (oldFolderName !== newFolderName) {
        diffLog['Shape:Folder'] = { old: oldFolderName, new: newFolderName };
      }

      // Shape Name diff (friendly properties should show Shape Name rename)
      const oldShapeName = sOld?.name as string | undefined;
      const newShapeName = sNew?.name as string | undefined;
      if (oldShapeName !== newShapeName) {
        diffLog['Shape:Name'] = { old: oldShapeName, new: newShapeName };
      }

      // Determine if selected element is created in diff (not present in reference state)
      const existedInReference = Array.isArray(
        (refCtx as any)?.loadedShapes?.[modelUrn]
      )
        ? (refCtx as any).loadedShapes[modelUrn].some(
            (s: any) => s?.dbId === dbId
          )
        : Array.isArray((refCtx as any)?.loadedSheetShapes?.[modelUrn])
        ? (refCtx as any).loadedSheetShapes[modelUrn].some(
            (s: any) => s?.dbId === dbId
          )
        : false;
      const isCreated = !existedInReference;

      // Geometry: recompute all algorithms for completeness
      const algorithms = geometricAlgorithmTypes;

      // (Removed ref/post points debug)
      const computeValue = async (
        ctx: PropertyContext,
        algo: any
      ): Promise<number | undefined> => {
        const results = await calculateGeometry(
          algo,
          elementDbIds,
          ctx,
          cleanedSettings
        );
        // (Removed per-algorithm geometry results debug)
        const numericValues = results
          .map((r) => r[0]?.value as number | undefined)
          .filter((v): v is number => typeof v === 'number');
        if (numericValues.length === 0) return undefined;
        const sum = numericValues.reduce((a, b) => a + b, 0);
        return isFinite(sum) ? sum : undefined;
      };
      for (const algo of algorithms) {
        let oldVal: number | undefined;
        let newVal: number | undefined;

        if (canResolveModel || isShapeSelection) {
          const res = await Promise.all([
            computeValue(oldCtx, algo),
            computeValue(newCtx, algo),
          ]);
          oldVal = res[0];
          newVal = res[1];
        } else {
          oldVal = undefined;
          newVal = undefined;
        }
        // (Removed geometry sums debug)
        if (isCreated) {
          if (newVal !== undefined) {
            diffLog[String(algo)] = { new: newVal };
          }
        } else if (oldVal !== newVal) {
          diffLog[String(algo)] = { old: oldVal, new: newVal };
        }
      }

      // Sparkel propertyValue diffs directly from diff
      Object.entries(diff).forEach(([key, value]) => {
        const m = key.match(
          /^propertyContext\.loadedSparkelProperties\.([^.]+)\.([^.]+)\.propertyValue$/
        );
        if (!m) return;
        const [, urn] = m;
        if (urn !== modelUrn) return;
        if (value && (value as any).old !== (value as any).new) {
          const propId = m[2];
          diffLog[`Sparkel:${propId}`] = {
            old: (value as any).old,
            new: (value as any).new,
          };
        }
      });

      return diffLog;
    },
  };
};

function addItems<T>(items: any, resultMap: Map<string, T>) {
  if (!items) return;
  if (Array.isArray(items)) {
    items.forEach((item: any) => {
      if (item && item.id) resultMap.set(item.id, item as T);
    });
  } else if (typeof items === 'object') {
    Object.values(items).forEach((item: any) => {
      if (item && item.id) resultMap.set(item.id, item as T);
    });
  }
}

// using refstate and diff, determine the models to show
export function getHistoricalActiveModels(
  refState: any,
  diff: Record<string, any>
) {
  // Grab the activeModels from the reference state
  // [refstate, other]
  const modelsMap = new Map<string, ModelsDeepFragment>();

  const refActiveModels = refState.activeModels;
  addItems<ModelsDeepFragment>(refActiveModels, modelsMap);

  const collectionDiff = diff?.['activeModels'];
  if (collectionDiff) {
    addItems<ModelsDeepFragment>(collectionDiff.old, modelsMap);
    addItems<ModelsDeepFragment>(collectionDiff.new, modelsMap);
  }

  // Also handle per-model diff keys like: activeModels.<modelId>
  Object.entries(diff).forEach(([key, value]) => {
    const match = key.match(/^activeModels\.([^.]+)$/);
    if (!match) return;

    const modelCandidate = value?.new;
    if (
      modelCandidate &&
      typeof modelCandidate === 'object' &&
      modelCandidate.id
    ) {
      modelsMap.set(
        modelCandidate.id,
        parseJson(modelCandidate) as ModelsDeepFragment
      );
    }
  });

  return Array.from(modelsMap.values());
}

// Build historical sheets list and per-name event types from referenceState and diff
type LoadedSheetEntry = {
  id: string;
  name: string;
  filename: string;
  version?: number;
};

const normalizeLoadedSheets = (value: unknown): LoadedSheetEntry[] => {
  if (Array.isArray(value)) return value as LoadedSheetEntry[];
  if (value && typeof value === 'object')
    return Object.values(value as Record<string, LoadedSheetEntry>);
  return [];
};

const buildSheetMap = (
  entries: LoadedSheetEntry[],
  projectId: string
): Map<string, Sheet> => {
  const map = new Map<string, Sheet>();
  entries.forEach((e) => {
    map.set(e.id, {
      id: e.id,
      name: e.name,
      filename: e.filename,
      version: e.version,
      projectId,
      createdAt: '',
      updatedAt: '',
      sheetPageCalibrations: [],
      s3Key: '',
    } as unknown as Sheet);
  });
  return map;
};

const collectPerIdNewEntries = (
  diff: Record<string, unknown>
): LoadedSheetEntry[] => {
  const out: LoadedSheetEntry[] = [];
  Object.entries(diff).forEach(([key, value]) => {
    const match = key.match(/^propertyContext\.loadedSheets\.([^.]+)$/);
    if (!match) return;
    const v = value as { old?: unknown; new?: unknown };
    if (v && v.new && typeof v.new === 'object') {
      const parsed = parseJson(v.new) as Partial<LoadedSheetEntry>;
      if (parsed && parsed.id && parsed.name && parsed.filename) {
        out.push(parsed as LoadedSheetEntry);
      }
    }
  });
  return out;
};

const chooseLatestPerName = (
  allSheets: Sheet[],
  preferredIds: Map<string, string>
): Sheet[] => {
  const chosenByName = new Map<string, Sheet>();
  allSheets.forEach((sheet) => {
    const name = sheet.name;
    if (!name) return;
    const preferId = preferredIds.get(name);
    if (!chosenByName.has(name)) {
      chosenByName.set(name, sheet);
    }
    if (preferId && sheet.id === preferId) {
      chosenByName.set(name, sheet);
    }
  });
  return Array.from(chosenByName.values());
};

const buildEventTypesByName = (
  sheets: Sheet[],
  referenceNames: Set<string>,
  newNames: Set<string>
): Record<string, EventType | undefined> => {
  const map: Record<string, EventType | undefined> = {};
  sheets.forEach((s) => {
    const name = s.name;
    if (!name) return;
    const hasOld = referenceNames.has(name);
    const hasNew = newNames.has(name);
    if (hasOld && hasNew) map[name] = EventType.Modified;
    else if (hasNew && !hasOld) map[name] = EventType.Created;
    else if (hasOld && !hasNew) map[name] = EventType.Deleted;
    else map[name] = undefined;
  });
  return map;
};

export function getHistoricalSheets(
  referenceState: unknown,
  diff: Record<string, unknown>,
  projectId: string
): { sheets: Sheet[]; eventTypeByName: Record<string, EventType | undefined> } {
  const ref = referenceState as Record<string, unknown> | undefined;
  const diffObj = diff as Record<string, unknown>;
  const byId = new Map<string, Sheet>();

  // 1) Start with reference state's loadedSheets (can be array or object map)
  const referenceStateLoadedSheets = (
    ref?.propertyContext as Record<string, unknown> | undefined
  )?.loadedSheets as unknown;
  const referenceEntries = normalizeLoadedSheets(referenceStateLoadedSheets);
  buildSheetMap(referenceEntries, projectId).forEach((v, k) => byId.set(k, v));

  // 2) Merge in collection-level diff new entries
  const loadedSheetsCollection = diffObj['propertyContext.loadedSheets'] as
    | { old?: unknown; new?: unknown }
    | undefined;
  if (loadedSheetsCollection && loadedSheetsCollection.new) {
    const newEntries = normalizeLoadedSheets(loadedSheetsCollection.new);
    buildSheetMap(newEntries, projectId).forEach((v, k) => byId.set(k, v));
  }

  // 3) Merge in per-id new entries: propertyContext.loadedSheets.<id>
  const perIdNewEntries = collectPerIdNewEntries(diffObj);
  buildSheetMap(perIdNewEntries, projectId).forEach((v, k) => byId.set(k, v));

  // 4) Deduplicate by name, prefer the id from new entries for that name
  const newIdsByName = new Map<string, string>();
  const newCollectionEntries =
    (loadedSheetsCollection && loadedSheetsCollection.new) || undefined;
  const newArrayFromCollection = normalizeLoadedSheets(newCollectionEntries);
  newArrayFromCollection.forEach((s) => {
    if (s && s.name && s.id) newIdsByName.set(s.name, s.id);
  });
  perIdNewEntries.forEach((s) => {
    if (s && s.name && s.id) newIdsByName.set(s.name, s.id);
  });

  const allSheets = Array.from(byId.values());
  const sheetsResult = chooseLatestPerName(allSheets, newIdsByName);

  // 5) Build per-name event types using reference vs new presence
  const referenceNames = new Set(referenceEntries.map((s) => s.name));
  const newNames = new Set<string>();
  newArrayFromCollection.forEach((s) => {
    if (s && s.name) newNames.add(s.name);
  });
  perIdNewEntries.forEach((s) => {
    if (s && s.name) newNames.add(s.name);
  });

  const eventTypeByName = buildEventTypesByName(
    sheetsResult,
    referenceNames,
    newNames
  );

  const explicitDeletedIds = computeExplicitDeletedSheetIds(diffObj);
  // Remove Deleted when not explicitly present in diff
  sheetsResult.forEach((sheet) => {
    const name = sheet.name;
    if (!name) return;
    if (
      eventTypeByName[name] === EventType.Deleted &&
      !explicitDeletedIds.has(sheet.id)
    ) {
      eventTypeByName[name] = undefined;
    }
  });

  return { sheets: sheetsResult, eventTypeByName };
}

function computeExplicitDeletedSheetIds(
  diffObj: Record<string, unknown>
): Set<string> {
  const ids = new Set<string>();
  // eslint-disable-next-line complexity
  Object.entries(diffObj).forEach(([key, value]) => {
    // Match loadedSheets.<id> with old present and no new
    const m = key.match(/^propertyContext\.loadedSheets\.([^.]+)$/);
    if (m) {
      const v = value as { old?: unknown; new?: unknown };
      if (v && v.old !== undefined && (v.new === undefined || v.new === null)) {
        ids.add(m[1]);
      }
    }
    // Also consider calibrations-based deletion signal
    const mCal = key.match(
      /^propertyContext\.loadedSheetCalibrations\.([^.]+)$/
    );
    if (mCal) {
      const v = value as { old?: unknown; new?: unknown };
      if (v && v.old !== undefined && (v.new === undefined || v.new === null)) {
        ids.add(mCal[1]);
      }
    }
  });
  return ids;
}
// We need to do the subrows merging dance
export function mergeEntries(
  oE1: ResolvedOrderEntry,
  oE2: ResolvedOrderEntry
): ResolvedOrderEntry {
  // Merge the resolvedElementidentifiersDbids
  const dbidsUnion = new Map<string, ElementIdentifierDbidType>(
    oE2.resolvedElementIdentifiersDbid?.map((eId) => [eId.modelUrn, eId])
  );
  for (const elementId of oE1.resolvedElementIdentifiersDbid ?? []) {
    const dbidInUnion = dbidsUnion.get(elementId.modelUrn);
    if (!dbidInUnion) {
      // if we do not have a corresponding entry, add one
      dbidsUnion.set(elementId.modelUrn, elementId);
      continue;
    }
    const otherDbids = dbidInUnion.dbIds;
    const dbidsUnionSet = new Set([...elementId.dbIds, ...otherDbids]);
    dbidsUnion.set(elementId.modelUrn, {
      modelUrn: elementId.modelUrn,
      dbIds: [...dbidsUnionSet.values()],
    });
  }

  // Copy top-level fields from oE1
  const merged: ResolvedOrderEntry = {
    ...oE1,
    resolvedElementIdentifiersDbid: [...dbidsUnion.values()],
    subRows: mergeSubRows(oE1.subRows ?? [], oE2.subRows ?? []),
  };

  return merged;
}

function mergeSubRows(
  subRows1: ResolvedOrderEntry[],
  subRows2: ResolvedOrderEntry[]
): ResolvedOrderEntry[] {
  const map = new Map<string, ResolvedOrderEntry>();

  // First, add all subRows from the first entry
  for (const sr of subRows1) {
    map.set(sr.name, sr);
  }

  // Then, merge in subRows from the second entry
  for (const sr of subRows2) {
    if (map.has(sr.name)) {
      // Recursive merge if names match
      const existing = map.get(sr.name)!;
      map.set(sr.name, mergeEntries(existing, sr));
    } else {
      // Add if it's unique
      map.set(sr.name, sr);
    }
  }

  // Return aggregated subRows
  return Array.from(map.values());
}

export function getHistoricalEntriesForOrder(
  orderId: string,
  resolvedStates: [any, any]
) {
  const entriesMap = new Map<string, ResolvedOrderEntry>();
  const [refState, latestState] = resolvedStates;

  const entriesFromLatestState = latestState?.project?.orders
    ?.flatMap((order: OrderDeepFragment) => order.orderEntries)
    ?.filter((entry: ResolvedOrderEntry) => entry.orderId === orderId);
  const entriesFromRefState = refState?.project?.orders
    ?.flatMap((order: OrderDeepFragment) => order.orderEntries)
    ?.filter((entry: ResolvedOrderEntry) => entry.orderId === orderId);

  if (entriesFromLatestState) {
    for (const entry of entriesFromLatestState) {
      const entryInRef = entriesFromRefState?.find(
        (eInRef: ResolvedOrderEntry) => eInRef.id === entry.id
      );
      let currentEntry = entry;
      if (entryInRef) {
        currentEntry = mergeEntries(entry, entryInRef);
      }
      entriesMap.set(entry.id, currentEntry);
    }
  }

  if (entriesFromRefState) {
    for (const entry of entriesFromRefState) {
      if (!entriesMap.has(entry.id)) {
        entriesMap.set(entry.id, entry);
      }
    }
  }
  return Array.from(entriesMap.values());
}

export function getHistoricalOrderColumns(
  orderId: string,
  resolvedStates: [any, any]
) {
  const columnsMap = new Map<string, OrderColumnDeepFragment>();
  resolvedStates.forEach((state) => {
    const columnsFromState = state?.project?.orders
      ?.flatMap((order: OrderDeepFragment) => order.orderColumns)
      ?.filter((column: OrderColumnDeepFragment) => column.orderId === orderId);
    addItems(columnsFromState, columnsMap);
  });
  return Array.from(columnsMap.values());
}
